<template>
    <div>
        <page-title
            :title="$t('deletedRecords.places')"
            :showTitle="false"
        />
        <div class="card">
            <div class="card-header bg-primary text-white">
                <div class="card-title h5 m-0">
                    {{ $t('deletedRecords.places') }}
                </div>
            </div>

            <div class="card-body">
                <div class="btn-toolbar justify-content-between mt-0 mb-2" role="toolbar">
                    <div class="form-inline col-6" />
                    <div class="input-group">
                        <input
                            v-model="searchQuery"
                            type="text"
                            v-on:keyup.enter.stop="massSearch"
                            v-on:keyup.esc.stop="clearFilters"
                            class="form-control"
                            aria-label="Mass search input"
                            aria-describedby="massSearch"
                        />
                        <button
                            class="btn btn-secondary"
                            @click.stop="massSearch"
                        >
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button
                            class="btn btn-secondary"
                            @click.stop="clearFilters"
                        >
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <kendo-datasource ref="datasource1"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total '"
                                  :schema-data="'data'"
                                  :page-size='defaultGridRows'
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError">
                </kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource1'"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable">
                    <kendo-grid-column
                        :template="btnsTemplate"
                        field="btnsColumn"
                        :title="' '"
                        :reorderable="false"
                        :sortable="false"
                        :filterable="false"
                        :lockable="false"
                        :locked="false"
                        :width="80">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'id'"
                                       :title="$t('registers.organizers.gridHeaders.id')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'name'"
                                       :title="$t('registers.organizers.gridHeaders.name')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'contactPerson'"
                                       :title="$t('contactPerson')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'phone'"
                                       :title="$t('person.phone')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'mobilePhone'"
                                       :title="$t('person.mobilePhone')">
                    </kendo-grid-column>
                </kendo-grid>
            </div>
        </div>
        <JsonFormatter
            v-model="dataReferences"
            :show.sync="showDataReferences"
            :titleKey="'buttons.links.tooltipText'"
            @onClose="onDataReferencesClose"
        >
        </JsonFormatter>
    </div>
</template>

<script>
import Vue from 'vue';
import store from '@/store/store';
import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
import PageTitle from '@/components/common/PageTitle';
import { mapGetters } from "vuex";
import dataReferencesService from "@/services/dataReferences.service";
import JsonFormatter from "@/components/common/json.formatter.modal.vue";

export default {
    components: {
        PageTitle,
        JsonFormatter
    },
    data() {
        return {
            defaultGridRows: this.$store.getters.defaultGridRows,
            searchQuery: '',
            excludedColumnsForSearch: [],
            schemaModelFields: {
                id: {
                    name: 'id',
                    type: 'number',
                    editable: false,
                    nullable: false,
                    visible: true,
                    titleKey: 'registers.organizers.gridHeaders.id'
                },
                name: {
                    name: 'name',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: 'registers.organizers.gridHeaders.name'
                },
                contactPerson: {
                    name: 'contactPerson',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: 'contactPerson'
                },
                phone: {
                    name: 'phone',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: 'person.phone'
                },
                mobilePhone: {
                    name: 'mobilePhone',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true,
                    titleKey: 'person.mobilePhone'
                }
            },
            transportSettings: {
                read: {
                    url: `${store.getters.baseUrl}/api/places/listDeleted`,
                    dataType: 'json',
                    type: 'POST',
                    contentType: 'application/json',
                    beforeSend: this.beforeSend
                },
                parameterMap: this.parameterMap
            },
            dataReferences: "",
            showDataReferences: false,
        }
    },
    computed: {
        ...mapGetters(["claims", "isEnVersion", "gridOptions", 'dataReferencesListCount']),
        token: function () {
            return store.getters.authToken;
        },
    },
    methods: {
        dataSourceError(e) {
            this.$vs.notify({
                title: this.$t('notifications.error'),
                text: this.$helpers.kendoDataSourceErrorText(e),
                color: 'danger',
                time: this.$store.getters.notificationDuration,
            });
        },
        parameterMap: function (options, operation) {
            if (operation !== "read" && options.models) {
                return { models: kendo.stringify(options.models) };
            } else {
                return kendo.stringify(options);
            }
        },
        beforeSend: function (req) {
            if (this.token) {
                req.setRequestHeader("Authorization", "Bearer " + this.token);
            }
        },
        btnsTemplate: function (e) {
            return {
                template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                templateArgs: {
                    ...e,
                    parentComponent: this.$refs.grid,
                    btns: [
                        {
                            name: "details",
                            text: "",
                            tooltip: this.$t("buttons.details.tooltipText"),
                            class: "btn btn-info",
                            icon: "fas fa-eye",
                            show: this.claims.venuesRegister.read,
                            clickHandler: this.detailsBtnClick,
                        },
                        {
                            name: "detailsInNewTab",
                            text: "",
                            tooltip: this.$t("buttons.detailsInNewTab.btnText"),
                            class: "btn btn-primary",
                            icon: "fas fa-eye",
                            show: this.claims.venuesRegister.read,
                            clickHandler: this.detailsInTewTabBtnClick,
                        },
                        {
                            name: "undelete",
                            text: "",
                            tooltip: this.$t("buttons.undelete.tooltipText"),
                            class: "btn btn-success",
                            icon: "fas fa-check",
                            show: this.claims.deletedRecords.update,
                            clickHandler: this.undeleteBtnClick,
                        },
                        {
                            name: "checkLinks",
                            text: "",
                            tooltip: this.$t("buttons.links.btnText"),
                            class: "btn btn-info",
                            icon: "fa fa-link",
                            show: this.claims.dataReferences.read,
                            clickHandler: this.dataReferencesBtnClick,
                        },
                    ],
                },
            };
        },
        refresh() {
            const grid = this.$refs.grid.kendoWidget();
            if (grid) {
                grid.dataSource.read();
            }
        },
        clearFilters() {
            this.searchQuery = "";
            const grid = this.$refs.grid.kendoWidget();
            if (grid) {
                grid.dataSource.sort({});
                grid.dataSource.page(1);
                grid.dataSource.aggregate([]);
                grid.dataSource.filter({});
            }
        },
        massSearch() {
            if (!this.searchQuery) {
                this.$vs.notify({
                    title: this.$t("buttons.search.btnText"),
                    text: this.$t("emptySearchFilter"),
                    color: "warning",
                    time: this.$store.getters.notificationDuration,
                });

                return;
            }

            let grid = this.$refs.grid.kendoWidget();
            if (grid) {
                let filter = this.$helpers.getMassFilterForGrid(
                    grid,
                    this.searchQuery,
                    this.excludedColumnsForSearch
                );
                if (filter) {
                    grid.dataSource.filter(filter);
                }
            }
        },
        detailsBtnClick(args) {
            this.$router.push(`/registers/place/${args.id}/details?returnUrl=${'/administration/deletedRecords/places'}`);
        },
        detailsInTewTabBtnClick(args) {
            let routeData = this.$router.resolve({
                name: "placesDetails",
                params: { id: args.id },
                query: {
                    returnUrl: '/administration/deletedRecords/places'
                }
            });
            window.open(routeData.href, "_blank");
        },
        undeleteBtnClick(args) {
            kendo
                .confirm(`${this.$t("buttons.undelete.confirmationText1", { name: args.name })}`)
                .then(
                    function () {
                        this.undeleteVenues(args.id);
                    }.bind(this)
                );
        },
        undeleteVenues(id) {
            this.$http
                .delete("/api/places/undelete", { params: { id: id } })
                .then((response) => {
                    this.refresh();

                    this.$vs.notify({
                        title: "",
                        text: this.$helpers.responseMessage(response),
                        color: "success",
                        time: this.$store.getters.notificationDuration,
                    });
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t(
                            "request.response.status." +
                                this.$helpers.errorStatusCode(error)
                        ),
                        text: this.$helpers.errorText(error),
                        color: "danger",
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        dataReferencesBtnClick(args) {
            if (!this.claims.dataReferences.read) {
                return this.$router.push(`/unauthorized`);
            }

            return this.getDataReferences(args.id);
        },
        getDataReferences(id) {
            dataReferencesService
                .checkReferences("Registers", "Place", id, this.dataReferencesListCount, true)
                .then((response) => {
                    if (response.data) {
                        this.dataReferences = response.data;
                    }

                    this.showDataReferences = true;
                })
                .catch((error) => {
                    console.log(error);
                });
        },
        onDataReferencesClose() {
            this.dataReferences = "";
            this.showDataReferences = false;
        },
    },
}
</script>
